import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaArrowRight,
} from "react-icons/fa";

export default function TopRatedHotels({ limit = 4 }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);

    const navigate = useNavigate();

    // =========================================================
    // WISHLIST
    // =========================================================

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem(
            "wishlist",
            JSON.stringify(updated)
        );
    };

    const toggleWishlist = (hotel) => {
        const isLoggedIn = true; // replace later with auth

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        let updated;

        if (exists) {
            updated = wishlist.filter(
                (item) => item.id !== hotel.id
            );
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(
            (item) => item.id === id
        );
    };

    // =========================================================
    // FETCH TOP RATED HOTELS
    // =========================================================

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get(
                    "https://marinadubai.com/api/top-rated-hotels"
                );

                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    // =========================================================
    // RENDER STARS
    // =========================================================

    const renderStars = (rating) => {
        const stars = [];
        const safeRating = Number(rating) || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(
                    <FaStar
                        key={i}
                        className="text-[#D5A33C]"
                    />
                );
            } else if (
                i === Math.floor(safeRating) &&
                safeRating % 1 >= 0.5
            ) {
                stars.push(
                    <FaStarHalfAlt
                        key={i}
                        className="text-[#D5A33C]"
                    />
                );
            } else {
                stars.push(
                    <FaRegStar
                        key={i}
                        className="text-[#D9D4C8]"
                    />
                );
            }
        }

        return stars;
    };

    // =========================================================
    // GET DISCOUNT
    // =========================================================

    const getDiscount = (hotel) => {
        if (hotel.discount) {
            return hotel.discount;
        }

        if (hotel.discount_percentage) {
            return hotel.discount_percentage;
        }

        return null;
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return (
            <section
                className="
                    w-full
                    bg-[#F7F5EF]
                    px-4
                    py-12

                    sm:px-6
                    sm:py-14

                    lg:px-8
                    lg:py-16
                "
            >
                <div
                    className="
                        mx-auto
                        w-full
                        max-w-[1310px]
                    "
                >
                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-6

                            lg:grid-cols-[230px_1fr]
                            lg:gap-12
                        "
                    >
                        {/* Left Skeleton */}

                        <div className="space-y-4">
                            <div
                                className="
                                    h-3
                                    w-28
                                    rounded
                                    bg-[#E5E0D5]
                                    animate-pulse
                                "
                            />

                            <div
                                className="
                                    h-12
                                    w-full
                                    rounded
                                    bg-[#E5E0D5]
                                    animate-pulse
                                "
                            />

                            <div
                                className="
                                    h-20
                                    w-full
                                    rounded
                                    bg-[#E5E0D5]
                                    animate-pulse
                                "
                            />

                            <div
                                className="
                                    h-10
                                    w-32
                                    rounded-full
                                    bg-[#E5E0D5]
                                    animate-pulse
                                "
                            />
                        </div>

                        {/* Cards Skeleton */}

                        <div
                            className="
                                grid
                                grid-cols-1
                                gap-4

                                sm:grid-cols-2

                                xl:grid-cols-4
                            "
                        >
                            {[1, 2, 3, 4].map(
                                (item) => (
                                    <div
                                        key={item}
                                        className="
                                            overflow-hidden
                                            rounded-xl
                                            border
                                            border-[#E9E4D9]
                                            bg-white
                                        "
                                    >
                                        <div
                                            className="
                                                h-[180px]
                                                bg-[#E5E0D5]
                                                animate-pulse
                                            "
                                        />

                                        <div
                                            className="
                                                space-y-3
                                                p-4
                                            "
                                        >
                                            <div
                                                className="
                                                    h-4
                                                    rounded
                                                    bg-[#E5E0D5]
                                                    animate-pulse
                                                "
                                            />

                                            <div
                                                className="
                                                    h-3
                                                    w-2/3
                                                    rounded
                                                    bg-[#E5E0D5]
                                                    animate-pulse
                                                "
                                            />

                                            <div
                                                className="
                                                    h-5
                                                    w-1/2
                                                    rounded
                                                    bg-[#E5E0D5]
                                                    animate-pulse
                                                "
                                            />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <section
            className="
                w-full
                bg-[#F7F5EF]

                px-4
                py-12

                sm:px-6
                sm:py-14

                lg:px-8
                lg:py-16

                xl:py-20
            "
        >
            <div
                className="
                    mx-auto
                    w-full
                    max-w-[1310px]
                "
            >

                {/* =================================================
                    MAIN LAYOUT
                ================================================= */}

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-8

                        lg:grid-cols-[220px_minmax(0,1fr)]
                        lg:items-center
                        lg:gap-8

                        xl:grid-cols-[235px_minmax(0,1fr)]
                        xl:gap-12
                    "
                >

                    {/* =================================================
                        LEFT INTRO
                    ================================================= */}

                    <div
                        className="
                            flex
                            flex-col
                            items-start
                        "
                    >

                        {/* Small Section Label */}

                        <div
                            className="
                                mb-4
                                flex
                                items-center
                                gap-3
                            "
                        >
                            <span
                                className="
                                    h-[2px]
                                    w-6
                                    bg-[#D5A33C]
                                "
                            />

                            <span
                                className="
                                    text-[9px]
                                    font-semibold
                                    uppercase
                                    tracking-[0.18em]
                                    text-[#496665]
                                "
                            >
                                Featured Hotels
                            </span>
                        </div>

                        {/* Heading */}

                        <h2
                            className="
                                max-w-[230px]

                                font-serif
                                text-[30px]
                                font-medium
                                leading-[1.08]
                                tracking-[-0.02em]

                                text-[#0B5552]

                                sm:text-[34px]

                                lg:text-[35px]

                                xl:text-[38px]
                            "
                        >
                            Popular Hotels
                            <br />
                            in Dubai
                        </h2>

                        {/* Description */}

                        <p
                            className="
                                mt-4
                                max-w-[225px]

                                text-[11px]
                                leading-[1.7]

                                text-[#617675]

                                sm:text-[12px]
                            "
                        >
                            Handpicked stays for an
                            extraordinary experience
                            in the world's most iconic
                            city.
                        </p>

                        {/* View All Button */}

                        <button
                            type="button"
                            onClick={() =>
                                navigate("/hotels")
                            }
                            className="
                                group
                                mt-6
                                inline-flex
                                items-center
                                gap-3

                                rounded-full

                                bg-[#D5A33C]

                                px-5
                                py-3

                                text-[11px]
                                font-semibold
                                text-[#173F3E]

                                shadow-[0_5px_15px_rgba(213,163,60,0.18)]

                                transition-all
                                duration-300

                                hover:bg-[#C9952F]
                                hover:shadow-[0_8px_20px_rgba(213,163,60,0.25)]

                                active:scale-[0.98]
                            "
                        >
                            <span>
                                View All Hotels
                            </span>

                            <FaArrowRight
                                className="
                                    text-[10px]

                                    transition-transform
                                    duration-300

                                    group-hover:translate-x-1
                                "
                            />
                        </button>
                    </div>

                    {/* =================================================
                        HOTEL CARDS
                    ================================================= */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-4

                            sm:grid-cols-2

                            xl:grid-cols-4
                        "
                    >
                        {hotels
                            .slice(0, limit)
                            .map((hotel, index) => {

                                const discount =
                                    getDiscount(hotel);

                                return (
                                    <div
                                        key={hotel.id}
                                        className="
                                            group
                                            relative

                                            overflow-hidden

                                            rounded-[10px]

                                            border
                                            border-[#E7E2D8]

                                            bg-white

                                            shadow-[0_4px_15px_rgba(16,58,57,0.06)]

                                            transition-all
                                            duration-300

                                            hover:-translate-y-1
                                            hover:shadow-[0_12px_28px_rgba(16,58,57,0.12)]
                                        "
                                    >

                                        {/* =====================================
                                            IMAGE
                                        ===================================== */}

                                        <div
                                            className="
                                                relative
                                                h-[185px]
                                                overflow-hidden
                                                cursor-pointer

                                                sm:h-[170px]

                                                lg:h-[165px]

                                                xl:h-[175px]
                                            "
                                            onClick={() =>
                                                navigate(
                                                    `/hotels/${hotel.slug}`
                                                )
                                            }
                                        >
                                            <img
                                                src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                                alt={hotel.name}
                                                className="
                                                    h-full
                                                    w-full
                                                    object-cover

                                                    transition-transform
                                                    duration-700

                                                    group-hover:scale-[1.06]
                                                "
                                            />

                                            {/* Image Overlay */}

                                            <div
                                                className="
                                                    pointer-events-none
                                                    absolute
                                                    inset-0

                                                    bg-gradient-to-t
                                                    from-[#092F31]/35
                                                    via-transparent
                                                    to-transparent
                                                "
                                            />

                                            {/* =================================
                                                TOP RATED BADGE
                                            ================================= */}

                                            {index === 0 && (
                                                <div
                                                    className="
                                                        absolute
                                                        left-2.5
                                                        top-2.5

                                                        rounded-full

                                                        bg-[#E9C46A]

                                                        px-2.5
                                                        py-1

                                                        text-[9px]
                                                        font-semibold
                                                        text-[#173F3E]

                                                        shadow-sm
                                                    "
                                                >
                                                    Top Rated
                                                </div>
                                            )}

                                            {/* =================================
                                                DISCOUNT
                                            ================================= */}

                                            {discount && (
                                                <div
                                                    className="
                                                        absolute
                                                        left-2.5
                                                        bottom-2.5

                                                        rounded-full

                                                        bg-[#0B5552]

                                                        px-2.5
                                                        py-1

                                                        text-[9px]
                                                        font-semibold
                                                        text-white

                                                        shadow-sm
                                                    "
                                                >
                                                    -
                                                    {String(
                                                        discount
                                                    ).replace(
                                                        "%",
                                                        ""
                                                    )}
                                                    %
                                                </div>
                                            )}

                                            {/* =================================
                                                WISHLIST
                                            ================================= */}

                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    toggleWishlist(
                                                        hotel
                                                    );
                                                }}
                                                className="
                                                    absolute
                                                    right-2.5
                                                    top-2.5

                                                    flex
                                                    h-8
                                                    w-8
                                                    items-center
                                                    justify-center

                                                    rounded-full

                                                    bg-white/95

                                                    shadow-md

                                                    transition-all
                                                    duration-200

                                                    hover:scale-110
                                                "
                                                aria-label={
                                                    isWishlisted(
                                                        hotel.id
                                                    )
                                                        ? "Remove from wishlist"
                                                        : "Add to wishlist"
                                                }
                                            >
                                                {isWishlisted(
                                                    hotel.id
                                                ) ? (
                                                    <FaHeart
                                                        className="
                                                            text-[13px]
                                                            text-[#C95C58]
                                                        "
                                                    />
                                                ) : (
                                                    <FaRegHeart
                                                        className="
                                                            text-[13px]
                                                            text-[#315C5B]
                                                        "
                                                    />
                                                )}
                                            </button>
                                        </div>

                                        {/* =====================================
                                            CARD CONTENT
                                        ===================================== */}

                                        <div
                                            className="
                                                p-3.5
                                                sm:p-3.5
                                            "
                                        >

                                            {/* Hotel Name */}

                                            <h3
                                                onClick={() =>
                                                    navigate(
                                                        `/hotels/${hotel.slug}`
                                                    )
                                                }
                                                title={hotel.name}
                                                className="
                                                    cursor-pointer

                                                    truncate

                                                    text-[13px]
                                                    font-semibold
                                                    leading-tight

                                                    text-[#123F3E]

                                                    transition-colors
                                                    duration-200

                                                    hover:text-[#B98227]
                                                "
                                            >
                                                {hotel.name}
                                            </h3>

                                            {/* Location */}

                                            <div
                                                className="
                                                    mt-2

                                                    flex
                                                    min-w-0
                                                    items-center
                                                    gap-1.5
                                                "
                                            >
                                                <FaMapMarkerAlt
                                                    className="
                                                        shrink-0
                                                        text-[10px]
                                                        text-[#0B5552]
                                                    "
                                                />

                                                <span
                                                    title={
                                                        hotel.location
                                                    }
                                                    className="
                                                        truncate

                                                        text-[10px]
                                                        text-[#788887]
                                                    "
                                                >
                                                    {
                                                        hotel.location
                                                    }
                                                </span>
                                            </div>

                                            {/* Rating */}

                                            <div
                                                className="
                                                    mt-2

                                                    flex
                                                    items-center
                                                    gap-2
                                                "
                                            >
                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-[2px]
                                                        text-[10px]
                                                    "
                                                >
                                                    {renderStars(
                                                        hotel.rating
                                                    )}
                                                </div>

                                                <span
                                                    className="
                                                        text-[10px]
                                                        font-semibold
                                                        text-[#526967]
                                                    "
                                                >
                                                    {hotel.rating ||
                                                        "0"}
                                                </span>

                                                <span
                                                    className="
                                                        text-[9px]
                                                        text-[#9AA3A1]
                                                    "
                                                >
                                                    rating
                                                </span>
                                            </div>

                                            {/* =================================
                                                BOTTOM PRICE ROW
                                            ================================= */}

                                            <div
                                                className="
                                                    mt-3
                                                    flex
                                                    items-end
                                                    justify-between
                                                    gap-2
                                                "
                                            >

                                                {/* Price */}

                                                <div>
                                                    <span
                                                        className="
                                                            block
                                                            text-[9px]
                                                            text-[#8B9694]
                                                        "
                                                    >
                                                        From
                                                    </span>

                                                    <div
                                                        className="
                                                            flex
                                                            items-baseline
                                                            gap-1
                                                        "
                                                    >
                                                        <span
                                                            className="
                                                                text-[17px]
                                                                font-bold
                                                                leading-none
                                                                text-[#0B5552]
                                                            "
                                                        >
                                                            AED{" "}
                                                            {Math.round(
                                                                hotel.price_per_night
                                                            )}
                                                        </span>

                                                        <span
                                                            className="
                                                                text-[9px]
                                                                text-[#7F8988]
                                                            "
                                                        >
                                                            / night
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Arrow */}

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(
                                                            `/hotels/${hotel.slug}`
                                                        )
                                                    }
                                                    className="
                                                        flex
                                                        h-8
                                                        w-8
                                                        shrink-0
                                                        items-center
                                                        justify-center

                                                        rounded-full

                                                        bg-[#F0ECE2]

                                                        text-[#0B5552]

                                                        transition-all
                                                        duration-300

                                                        hover:bg-[#D5A33C]
                                                        hover:text-[#173F3E]
                                                        hover:translate-x-0.5
                                                    "
                                                    aria-label={`View ${hotel.name}`}
                                                >
                                                    <FaArrowRight
                                                        className="
                                                            text-[10px]
                                                        "
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        </section>
    );
}